/* sc-cart-protection-toggle.js — add-back for the per-product Shipping
 * Protection row (V4 drawer).
 *
 * Contract (proved on DRAFT 2026-07-16, see plans/reports/qa addendum):
 * - Shopify Ajax CANNOT nest a new line under an EXISTING parent line
 *   (parent_id → 422 "Cannot reference existing parent lines by variant_id";
 *   parent_key / parent_relationship shapes are ignored). Native nested
 *   children exist only when Axe adds parent+child in one ATC call.
 * - The industry-standard cart tick adds the protection variant top-level
 *   and lets Shopify merge quantities. We improve on that: each tick adds
 *   ONE line carrying `_sc_protection_for: <parent line key>` so the state
 *   survives reload and renders under the exact parent. (Identical
 *   variant+property lines can still merge to qty>1 across tabs; the
 *   drawer routes any surplus through the visible order-level row.)
 *
 * Scope: BOTH toggle directions, each one serialized POST with bundled
 * section rendering, then swap the same regions the vendor swaps.
 * - (per-line re-offer checkbox removed 2026-08-19 — 6s-16. The cart-level
 *   upsell BLOCK (sc-cart-upsell-block.liquid, owner ref 'Goes with any bag')
 *   re-adds through the same proven capture→remove→re-add native bundle via
 *   [data-sc-cart-upsell-add] below.)
 * - checked→unchecked: POST /cart/change.js by line KEY. Never the vendor
 *   <cart-remove-button>/line-index path: with V4 markup the child lives
 *   inside its parent <li>, and the vendor treats a one-<li> drawer removal
 *   as "last item" and POSTs /cart/clear — reproduced wiping the whole cart.
 *   The sc-cart-remove-confirm modal still fronts the removal: it opens on
 *   the first click and re-clicks this button with the bypass flag set.
 * On failure the row shows the standard inline error and re-enables.
 *
 * Orphan sweep: the theme's own remove button / qty→0 paths know nothing
 * about `_sc_protection_for` lines, so removing a parent leaves its
 * protection line in the cart still charging (reproduced on draft
 * 2026-07-16: two parentless "Shipping Protection" cards after removing
 * both products). A drawer observer watches for server-rendered
 * `.sc-cart-protection-orphan-row` markers and reconciles: re-link the
 * line when the parent was re-added under a new key (Edit Design replaces
 * the line, same variant), otherwise remove it. Mutations wait for
 * `window.__scCartMutationPending` (sc-cart-mutation-recovery) so theme
 * requests in flight cannot shift 1-based line indexes under us.
 */
(function () {
  'use strict';

  var BYPASS_FLAG = '__sc_remove_confirmed'; // shared with sc-cart-remove-confirm.js
  var pending = false;

  // ---- Surfaces. The drawer exists on every page; /cart additionally
  // renders the page cart section + footer. Every mutation requests and
  // swaps ALL surfaces present so the two can never drift apart.
  function pageItemsRoot() {
    return document.querySelector('cart-items');
  }

  function pageItemsSectionId() {
    var el = document.getElementById('main-cart-items');
    return el ? el.getAttribute('data-id') : null;
  }

  function pageFooterSectionId() {
    var el = document.getElementById('main-cart-footer');
    return el ? el.getAttribute('data-id') : null;
  }

  function sectionsToRender() {
    var ids = ['cart-drawer', 'cart-icon-bubble'];
    var pageId = pageItemsSectionId();
    if (pageId) ids.push(pageId);
    var footerId = pageFooterSectionId();
    if (footerId) ids.push(footerId);
    return ids.join(',');
  }

  // While a toggle mutation is in flight the WHOLE cart mutation surface
  // locks — protection toggles AND the theme's quantity/remove controls,
  // drawer and page alike. A concurrent theme mutation landing between our
  // fresh cart read and the line-index change.js could shift 1-based indexes
  // and remove the wrong line. A successful swap replaces the DOM with fresh
  // enabled controls; error paths re-enable explicitly.
  function setTogglesDisabled(disabled) {
    var controls = document.querySelectorAll(
      '.sc-cart-protection__remove-icon, cart-drawer quantity-input button, cart-drawer quantity-input input, cart-drawer cart-remove-button button, cart-items quantity-input button, cart-items quantity-input input'
    );
    for (var i = 0; i < controls.length; i++) controls[i].disabled = disabled;
    // Page remove controls are anchors — `disabled` is a no-op there, and
    // pointer-events doesn't stop keyboard Enter: pull them out of the tab
    // order (and off focus) while the lock holds.
    var anchors = document.querySelectorAll('cart-items cart-remove-button a');
    for (var j = 0; j < anchors.length; j++) {
      anchors[j].style.pointerEvents = disabled ? 'none' : '';
      if (disabled) {
        anchors[j].setAttribute('aria-disabled', 'true');
        anchors[j].setAttribute('tabindex', '-1');
        if (document.activeElement === anchors[j]) anchors[j].blur();
      } else {
        anchors[j].removeAttribute('aria-disabled');
        anchors[j].removeAttribute('tabindex');
      }
    }
    // Checkout must not fire in the tick window where the parent line is
    // removed pending its bundled re-add. Only re-enable buttons THIS lock
    // disabled — a server-rendered disabled checkout (empty cart) stays put.
    var checkouts = document.querySelectorAll('#checkout, #CartDrawer-Checkout');
    for (var k = 0; k < checkouts.length; k++) {
      if (disabled) {
        if (!checkouts[k].disabled) {
          checkouts[k].disabled = true;
          checkouts[k].setAttribute('data-sc-locked', '');
        }
      } else if (checkouts[k].hasAttribute('data-sc-locked')) {
        checkouts[k].disabled = false;
        checkouts[k].removeAttribute('data-sc-locked');
      }
    }
  }

  // Setting a cached/carried preview src must never leave a broken image if
  // the CDN URL has expired — fall back to whatever was there before.
  function setSrcSafe(img, url) {
    if (!img || !url) return;
    var orig = img.getAttribute('src');
    if (orig === url) return;
    img.addEventListener('error', function onErr() {
      img.removeEventListener('error', onErr);
      if (orig) img.setAttribute('src', orig);
    });
    img.setAttribute('src', url);
  }

  // The drawer body scrolls independently — remember where the shopper was
  // so a toggle deep in a long cart doesn't jump back to the top.
  function findScroller() {
    var candidates = document.querySelectorAll('cart-drawer .cart-drawer__body, cart-drawer .drawer__contents, cart-drawer .drawer__inner');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].scrollHeight > candidates[i].clientHeight + 1) return candidates[i];
    }
    return null;
  }

  // The page-cart server markup still carries the stock mockup, so preserve
  // its personalized image, trigger attributes and Preview button across the
  // page's partial swap. Drawer responses now own their complete final preview
  // markup and must never receive state carried from an older drawer row.
  var PREVIEW_ATTRS = ['data-image', 'data-price', 'data-compare-price', 'data-product-type', 'data-title'];

  function capturePreviewState(rowSelector) {
    var state = {};
    document.querySelectorAll(rowSelector).forEach(function (rowEl) {
      var media = rowEl.querySelector('.cart-item__media.sc-preview-show');
      if (!media) return;
      var img = media.querySelector('img.cart-item__image');
      var btn = media.querySelector('.sc-cart-preview-btn--compact');
      var attrs = {};
      PREVIEW_ATTRS.forEach(function (n) {
        var v = media.getAttribute(n);
        if (v !== null) attrs[n] = v;
      });
      state[rowEl.getAttribute('data-cart-item-key')] = {
        src: img ? img.getAttribute('src') : null,
        btn: btn ? btn.outerHTML : null,
        attrs: attrs
      };
    });
    return state;
  }

  function restorePreviewState(state, rowSelector) {
    document.querySelectorAll(rowSelector).forEach(function (rowEl) {
      var kept = state[rowEl.getAttribute('data-cart-item-key')];
      if (!kept) return;
      var media = rowEl.querySelector('.cart-item__media');
      if (!media) return;
      if (kept.src) setSrcSafe(media.querySelector('img.cart-item__image'), kept.src);
      media.classList.add('sc-preview-show');
      Object.keys(kept.attrs).forEach(function (n) { media.setAttribute(n, kept.attrs[n]); });
      if (kept.btn && !media.querySelector('.sc-cart-preview-btn--compact')) {
        media.insertAdjacentHTML('beforeend', kept.btn);
      }
      media.dataset.scPreviewApplied = '1';
    });
  }

  var PAGE_ROWS = 'cart-items tr.cart-item[data-cart-item-key]';
  var ALL_ROWS = 'cart-drawer li.cart-drawer-item[data-cart-item-key], ' + PAGE_ROWS;

  // A companion add/remove replaces the drawer markup wholesale, so the parent line's
  // personalized preview <img> was recreated and re-decoded on EVERY toggle — a white
  // flash + shimmer over a design that did not change (owner oemm). Keep the actual
  // already-decoded img NODE per line key and graft it into the fresh markup whenever
  // the design (data-pz-state-id) is unchanged: no fetch, no decode, no flash.
  function captureRowImages() {
    var map = {};
    document.querySelectorAll(ALL_ROWS).forEach(function (rowEl) {
      var img = rowEl.querySelector('.cart-item__media img.cart-item__image');
      if (img && img.complete && img.naturalWidth > 0) {
        map[rowEl.getAttribute('data-cart-item-key')] = img;
      }
    });
    return map;
  }
  function restoreRowImages(map) {
    document.querySelectorAll(ALL_ROWS).forEach(function (rowEl) {
      var old = map[rowEl.getAttribute('data-cart-item-key')];
      if (!old) return;
      var fresh = rowEl.querySelector('.cart-item__media img.cart-item__image');
      if (!fresh || fresh === old || !fresh.parentNode) return;
      var oldState = old.getAttribute('data-pz-state-id');
      var freshState = fresh.getAttribute('data-pz-state-id');
      if (String(oldState || '') !== String(freshState || '')) return; // design changed — load normally
      old.className = fresh.className;
      old.classList.remove('sc-pz-thumb--loading');
      fresh.parentNode.replaceChild(old, fresh);
    });
  }

  function swapSections(sections, itemCount) {
    if (!sections) return;
    var scroller = findScroller();
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var keptImages = captureRowImages();
    if (sections['cart-drawer']) {
      var holder = document.createElement('div');
      holder.innerHTML = sections['cart-drawer'];
      var freshInner = holder.querySelector('.drawer__inner');
      var currentInner = document.querySelector('cart-drawer .drawer__inner');
      if (freshInner && currentInner) currentInner.innerHTML = freshInner.innerHTML;
      // Re-pin visual row order BEFORE the scroll restore below — the
      // bundled re-add moved this row to the top of the server order, and a
      // restore against that layout lets scroll anchoring drag the list.
      restoreRowImages(keptImages);
      if (window.scCartRowOrder) window.scCartRowOrder.apply();
    }
    // Page cart items: mirror the vendor swap — only .js-contents inside
    // #main-cart-items is replaced, the <cart-items> element and its
    // countdown stay untouched (theme structure preserved).
    var pageId = pageItemsSectionId();
    if (pageId && sections[pageId]) {
      var pagePreview = capturePreviewState(PAGE_ROWS);
      var pageHolder = document.createElement('div');
      pageHolder.innerHTML = sections[pageId];
      var freshContents = pageHolder.querySelector('#main-cart-items .js-contents');
      var currentContents = document.querySelector('#main-cart-items .js-contents');
      if (freshContents && currentContents) currentContents.innerHTML = freshContents.innerHTML;
      restorePreviewState(pagePreview, PAGE_ROWS);
      restoreRowImages(keptImages);
      // Re-pin visual row order before this frame paints — the bundled
      // re-add moved the ticked row to the top of the server order.
      if (window.scCartRowOrder) window.scCartRowOrder.apply();
      if (typeof itemCount === 'number') {
        var itemsRoot = pageItemsRoot();
        if (itemsRoot) itemsRoot.classList.toggle('is-empty', itemCount === 0);
      }
    }
    // Page footer subtotal block — sc-cart-footer.js re-applies its totals
    // rework + trust badges through its own MutationObserver after this.
    var footerId = pageFooterSectionId();
    if (footerId && sections[footerId]) {
      var footerHolder = document.createElement('div');
      footerHolder.innerHTML = sections[footerId];
      var freshFooter = footerHolder.querySelector('.cart__blocks .js-contents');
      var currentFooter = document.querySelector('#main-cart-footer .cart__blocks .js-contents');
      if (freshFooter && currentFooter) currentFooter.innerHTML = freshFooter.innerHTML;
      if (typeof itemCount === 'number') {
        var footerEl = document.getElementById('main-cart-footer');
        if (footerEl) footerEl.classList.toggle('is-empty', itemCount === 0);
      }
    }
    if (scrollTop) {
      var newScroller = findScroller();
      if (newScroller) newScroller.scrollTop = scrollTop;
    }
    if (sections['cart-icon-bubble']) {
      var bubbleHolder = document.createElement('div');
      bubbleHolder.innerHTML = sections['cart-icon-bubble'];
      var bubble = document.getElementById('cart-icon-bubble');
      var freshBubble = bubbleHolder.querySelector('#shopify-section-cart-icon-bubble') || bubbleHolder;
      if (bubble) bubble.innerHTML = freshBubble.innerHTML;
    }
    // is-empty lives on <cart-drawer>, outside the swapped inner — the theme
    // CSS collapses the empty-state block behind it (same pattern as the
    // existing customily cart enhancer).
    if (typeof itemCount === 'number') {
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', itemCount === 0);
    }
  }

  function showError(row, message) {
    if (!row) return;
    var errEl = row.querySelector('.cart-item__error-text');
    if (errEl) errEl.textContent = message || 'Could not update Shipping Protection. Please try again.';
  }

  // Light-green success notice shown after a successful tick/untick; also
  // mirrors into the surface's polite live region. Drawer: pinned below the
  // drawer header. Page: pinned to the top of the viewport (its own
  // modifier class in sc-cart-page.css). Hide/show timers live on each
  // toast element — the two surfaces must never clear each other's timer
  // (a shared timer left the fixed page toast stuck visible).
  function showToast(message, surface) {
    var host;
    var live;
    if (surface === 'page') {
      host = pageItemsRoot();
      live = document.getElementById('cart-live-region-text');
    } else {
      host = document.querySelector('cart-drawer .drawer__inner');
      live = document.getElementById('CartDrawer-LiveRegionText');
    }
    if (!host) return;
    var toast = host.querySelector('.sc-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = surface === 'page' ? 'sc-cart-toast sc-cart-toast--page' : 'sc-cart-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg><span></span>';
      host.appendChild(toast);
    }
    if (live) live.textContent = message;
    if (toast.__scToastTimer) clearTimeout(toast.__scToastTimer);
    var show = function () {
      toast.querySelector('span').textContent = message;
      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      toast.__scToastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 2600);
    };
    if (toast.classList.contains('is-visible')) {
      // Back-to-back toggles (untick → tick): both messages start with
      // "Shipping Protection…", so an in-place text swap reads as ONE
      // unchanged toast. Dip out first so the new notice is visible.
      toast.classList.remove('is-visible');
      toast.__scToastTimer = setTimeout(show, 220);
    } else {
      show();
    }
  }

  // Resolves once no theme-initiated cart mutation is in flight (flag kept
  // by sc-cart-mutation-recovery's fetch wrapper). Resolves `true` when idle,
  // `false` on timeout — user-initiated paths proceed anyway (their
  // post-verify fails closed), the background sweep must abort instead.
  function whenCartIdle(maxWaitMs) {
    return new Promise(function (resolve) {
      var waited = 0;
      (function check() {
        if (!window.__scCartMutationPending) { resolve(true); return; }
        if (waited >= maxWaitMs) { resolve(false); return; }
        waited += 120;
        setTimeout(check, 120);
      })();
    });
  }

  function protectionPointer(it) {
    return (it && it.properties && it.properties._sc_protection_for) || '';
  }

  // A PDP app layer redirects the page to /cart after any FORM-ENCODED
  // fetch to /cart/add (isolated on draft 2026-07-16: JSON fetch clean,
  // form fetch redirects, form XHR clean — ảnh #51). The bundled nested
  // re-add MUST be form-encoded (bracket parent_id shape), so it rides XHR,
  // which that layer ignores. Minimal Response-like shape for the chain.

  // Edit Design replaces the parent line under a NEW key (same variant, new
  // properties) — the paid protection must follow the product, not vanish.
  // Adoptable parent: same variant as the stale pointer, a real product line
  // (no pointer, no native parent_relationship), not already protected.
  function findAdoptiveParent(cart, orphan) {
    var wantedVariant = protectionPointer(orphan).split(':')[0];
    if (!wantedVariant) return null;
    var claimed = {};
    cart.items.forEach(function (it) {
      var ptr = (it.parent_relationship && it.parent_relationship.parent_key) || protectionPointer(it);
      if (ptr) claimed[ptr] = true;
    });
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (String(it.variant_id) !== wantedVariant) continue;
      if (protectionPointer(it) || it.parent_relationship) continue;
      if (claimed[it.key]) continue;
      return it;
    }
    return null;
  }

  // Sweep failed mid-mutation (e.g. re-link add failed after the removal
  // committed) — the DOM may still show rows for lines that no longer exist.
  // Re-render every surface from the Section Rendering API before unlocking.
  function resyncCartSections() {
    var url = window.location.pathname + '?sections=' + sectionsToRender();
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (sections) { swapSections(sections); })
      .catch(function () { /* next cart render self-heals */ });
  }

  function finishSweep(resync) {
    var done = function () {
      pending = false;
      setTogglesDisabled(false);
    };
    if (resync) { resyncCartSections().then(done, done); return; }
    done();
  }

  // One pass = reconcile ONE orphan (re-link or remove), then recurse on a
  // fresh cart read so line indexes are never reused across mutations.
  // Runs with `pending` held and controls disabled for the WHOLE sweep.
  function sweepPass(passesLeft) {
    whenCartIdle(4000)
      .then(function (idle) {
        // Background op: on contention abort and retry later — proceeding is
        // exactly when a stale index could delete a non-orphan line.
        if (!idle) { finishSweep(); scheduleOrphanSweep(); return null; }
        // The idle wait may have let a theme render replace the DOM with
        // enabled controls — re-assert the sweep lock before mutating.
        setTogglesDisabled(true);
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (res) { return res.json(); })
          .then(function (cart) {
            var byKey = {};
            var keyCount = {};
            (cart.items || []).forEach(function (it) {
              byKey[it.key] = true;
              keyCount[it.key] = (keyCount[it.key] || 0) + 1;
            });
            var orphan = null;
            var line = 0;
            var neverRelink = false;
            for (var i = 0; i < cart.items.length; i++) {
              var ptr = protectionPointer(cart.items[i]);
              if (ptr && !byKey[ptr]) { orphan = cart.items[i]; line = i + 1; break; }
            }
            if (!orphan) {
              // Second flavor: no pointer-orphan, but nothing REAL left to
              // protect. The drawer marks every protection line (nested,
              // orphan or order-level, any linkage flavor) with a keyed
              // toggle — when every cart line is one of those, the cart
              // holds only protection charging for nothing. Clean it up.
              // (Native children never orphan: Shopify removes them with
              // their parent — proved on draft 2026-07-16.)
              var domKeys = {};
              document.querySelectorAll('[data-sc-protection-remove][data-sc-protection-key]').forEach(function (btn) {
                domKeys[btn.getAttribute('data-sc-protection-key')] = true;
              });
              // Legacy/external adds (e.g. Axe "Before" strategy) can leave
              // an UNLINKED protection line with no marker row — the drawer
              // block and the page section both stamp the protection variant
              // id on their wrapper for these.
              var wrapper = document.querySelector('cart-drawer-items[data-sc-protection-variant], cart-items[data-sc-protection-variant]');
              var protectionVariant = wrapper ? wrapper.getAttribute('data-sc-protection-variant') : '';
              var items = cart.items || [];
              var protectionOnly = items.length > 0 && items.every(function (it) {
                return domKeys[it.key] || protectionPointer(it) || it.parent_relationship ||
                  (protectionVariant && String(it.variant_id) === protectionVariant);
              });
              if (protectionOnly) {
                orphan = items[0];
                line = 1;
                neverRelink = true; // nothing real to adopt it
              } else {
                finishSweep();
                return null;
              }
            }
            var adopter = neverRelink ? null : findAdoptiveParent(cart, orphan);
            var orphanKey = orphan.key;
            var orphanPtr = protectionPointer(orphan);
            // Unique key → key-based change.js, immune to index shifts from
            // concurrent tabs. Duplicated key (two native children of
            // different parents) → 1-based line index is the only handle.
            var removeBody = keyCount[orphanKey] === 1
              ? { id: orphanKey, quantity: 0 }
              : { line: line, quantity: 0 };
            if (!adopter) {
              removeBody.sections = sectionsToRender();
              removeBody.sections_url = window.location.pathname;
            }
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(removeBody)
            })
              .then(function (res) {
                if (!res.ok) throw new Error('sweep remove failed ' + res.status);
                return res.json();
              })
              .then(function (json) {
                var stillThere = (json.items || []).some(function (it) {
                  return it.key === orphanKey && protectionPointer(it) === orphanPtr;
                });
                // Index landed elsewhere (cross-tab shift) — stop, never loop.
                if (stillThere) throw new Error('sweep index shifted');
                if (!adopter) {
                  swapSections(json.sections, json.item_count);
                  setTogglesDisabled(true); // swap rebuilt controls enabled
                  if (passesLeft > 1) { sweepPass(passesLeft - 1); } else { finishSweep(); }
                  return null;
                }
                return fetch('/cart/add.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    items: [{
                      id: orphan.variant_id,
                      quantity: 1,
                      properties: { _sc_protection_for: adopter.key }
                    }],
                    sections: sectionsToRender(),
                    sections_url: window.location.pathname
                  })
                })
                  .then(function (res) {
                    if (!res.ok) throw new Error('sweep relink failed ' + res.status);
                    return res.json();
                  })
                  .then(function (json2) {
                    swapSections(json2.sections);
                    setTogglesDisabled(true); // swap rebuilt controls enabled
                    if (passesLeft > 1) { sweepPass(passesLeft - 1); } else { finishSweep(); }
                  });
              });
          });
      })
      .catch(function () { finishSweep(true); });
  }

  var sweepTimer = null;
  function scheduleOrphanSweep() {
    if (sweepTimer) clearTimeout(sweepTimer);
    sweepTimer = setTimeout(function () {
      sweepTimer = null;
      // A toggle op owns the cart right now; its swap re-renders the drawer
      // and the observer re-schedules this check afterwards.
      if (pending) return;
      if (!sweepCandidateInDom()) return;
      pending = true;
      setTogglesDisabled(true);
      sweepPass(4);
    }, 350);
  }

  function sweepCandidateInDom() {
    if (document.querySelector('.sc-cart-protection-orphan-row')) return true;
    // Unlinked protection lines render as NORMAL cards/rows (no marker);
    // spot them by the variant id their quantity input carries, per surface.
    var scopes = [
      { wrapper: 'cart-drawer-items[data-sc-protection-variant]', inputs: 'cart-drawer .cart-drawer-item [data-quantity-variant-id]' },
      { wrapper: 'cart-items[data-sc-protection-variant]', inputs: 'cart-items tr.cart-item [data-quantity-variant-id]' }
    ];
    for (var s = 0; s < scopes.length; s++) {
      var wrapper = document.querySelector(scopes[s].wrapper);
      if (!wrapper) continue;
      var v = wrapper.getAttribute('data-sc-protection-variant');
      var inputs = document.querySelectorAll(scopes[s].inputs);
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].getAttribute('data-quantity-variant-id') === v) return true;
      }
    }
    return false;
  }

  function installOrphanWatch() {
    scheduleOrphanSweep();
    if (typeof MutationObserver !== 'function') return;
    var roots = document.querySelectorAll('cart-drawer, cart-items');
    for (var i = 0; i < roots.length; i++) {
      new MutationObserver(function () {
        scheduleOrphanSweep();
      }).observe(roots[i], { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installOrphanWatch);
  } else {
    installOrphanWatch();
  }

  // URLSearchParams body via XHR — the bracket shape /cart/add.js proved to nest on draft.
  function postFormXhr(url, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.onload = function () {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: function () {
            try { return Promise.resolve(JSON.parse(xhr.responseText)); }
            catch (e) { return Promise.reject(e); }
          }
        });
      };
      xhr.onerror = function () { reject(new TypeError('Network request failed')); };
      xhr.send(body.toString());
    });
  }

  function focusParentToggle(parentRowKey, surface) {
    // Post-removal focus home. The re-offer toggle is gone (6s-16), so aim at
    // the parent line: a remaining nested-row trash icon first, else the
    // parent line's own remove control — keyboard users never lose focus into
    // <body> after the row they were on disappears.
    var target = null;
    var scope = surface === 'page' ? 'cart-items ' : 'cart-drawer ';
    if (parentRowKey) {
      var esc = window.CSS && CSS.escape ? CSS.escape(parentRowKey) : parentRowKey;
      var rowEl = document.querySelector(scope + '.cart-item[data-cart-item-key="' + esc + '"]');
      if (rowEl) {
        target = rowEl.querySelector('.sc-cart-protection__remove-icon')
          || rowEl.querySelector('cart-remove-button a, cart-remove-button button, button, a[href]');
      }
    }
    if (!target) target = document.querySelector(scope + '.sc-cart-protection__remove-icon');
    if (target && target.focus) target.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (e) {
    var removeBtn = e.target && e.target.closest && e.target.closest('[data-sc-protection-remove]');
    if (removeBtn) {
      // Without the flag, let sc-cart-remove-confirm's capture listener open
      // the confirmation modal; it re-clicks this button with the flag set.
      if (!removeBtn[BYPASS_FLAG]) return;
      e.preventDefault();
      e.stopPropagation();
      var removeRow = removeBtn.closest('.sc-cart-protection');
      if (pending) {
        // Modal was opened before another toggle started — never drop a
        // confirmed removal silently.
        showError(removeRow, 'Cart is updating. Please try again.');
        return;
      }
      var lineKey = removeBtn.getAttribute('data-sc-protection-key');
      var parentPointer = removeBtn.getAttribute('data-sc-protection-parent-pointer');
      var removeLabel = removeBtn.getAttribute('data-sc-protection-label') || 'Shipping Protection';
      if (!lineKey) return;
      // Drawer rows are li.cart-drawer-item.cart-item, page rows tr.cart-item
      // — .cart-item covers both; the surface picks toast/focus targets.
      var removeSurface = removeBtn.closest('cart-drawer') ? 'drawer' : 'page';
      var removeParentRow = removeBtn.closest('.cart-item');
      var removeParentRowKey = removeParentRow ? removeParentRow.getAttribute('data-cart-item-key') : null;
      pending = true;
      setTogglesDisabled(true);
      removeBtn.setAttribute('aria-busy', 'true');
      if (removeRow) removeRow.classList.add('updating');
      // Two native children of DIFFERENT parents can share one line key
      // (key = variant+properties; parent_relationship is excluded — proved
      // on draft), and change.js by key hits every line with that key.
      // Resolve the unique 1-based line index (key + parent pointer) from a
      // fresh cart read, then mutate that line only. Fail closed on mismatch.
      whenCartIdle(3000)
        .then(function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var line = 0;
          for (var i = 0; i < cart.items.length; i++) {
            var it = cart.items[i];
            if (it.key !== lineKey) continue;
            var ptr = (it.parent_relationship && it.parent_relationship.parent_key) ||
              (it.properties && it.properties._sc_protection_for) || '';
            if (!parentPointer || ptr === parentPointer) { line = i + 1; break; }
          }
          if (!line) throw new Error('line not found');
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: line,
              quantity: 0,
              sections: sectionsToRender(),
              sections_url: window.location.pathname
            })
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('remove failed ' + res.status);
          return res.json();
        })
        .then(function (json) {
          // Post-verify against the returned cart: if the target line is
          // still present the index landed elsewhere (e.g. a cross-tab
          // mutation shifted it) — surface it instead of pretending success.
          var stillThere = (json.items || []).some(function (it) {
            var ptr = (it.parent_relationship && it.parent_relationship.parent_key) ||
              (it.properties && it.properties._sc_protection_for) || '';
            return it.key === lineKey && (!parentPointer || ptr === parentPointer);
          });
          swapSections(json.sections, json.item_count);
          setTogglesDisabled(false);
          if (stillThere) {
            var liveRow = document.querySelector('[data-sc-protection-key]') ? document.querySelector('.sc-cart-protection') : null;
            showError(liveRow, 'Cart changed while updating. Please try again.');
          } else {
            showToast(removeLabel + ' removed', removeSurface);
            focusParentToggle(removeParentRowKey, removeSurface);
          }
        })
        .catch(function () {
          if (removeRow) removeRow.classList.remove('updating');
          removeBtn.removeAttribute('aria-busy');
          setTogglesDisabled(false);
          showError(removeRow, 'Could not remove ' + removeLabel + '. Please try again.');
        })
        .then(function () {
          pending = false;
        });
      return;
    }

    var addBtn = e.target && e.target.closest && e.target.closest('[data-sc-cart-upsell-add]');
    if (!addBtn) return;
    e.preventDefault();
    e.stopPropagation();
    if (addBtn.getAttribute('aria-busy') === 'true') return;

    var variantId = parseInt(addBtn.getAttribute('data-sc-upsell-variant'), 10);
    var parentKey = addBtn.getAttribute('data-sc-upsell-parent');
    var addLabel = addBtn.getAttribute('data-sc-upsell-label') || 'Add-on';
    if (!variantId || !parentKey) return;

    // BATCHED (owner: "cho add đồng thời nhiều upsell"): taps within the debounce window —
    // or landing while a flight is airborne — queue up and ship in ONE capture→remove→
    // re-add bundle instead of one round trip each.
    addBtn.setAttribute('aria-busy', 'true');
    var row = addBtn.closest('.sc-cart-upsell__row');
    if (row) row.classList.add('updating');
    var q = upsellQueue[parentKey] || (upsellQueue[parentKey] = { items: [], surface: null, timer: null });
    var dup = false;
    for (var qi = 0; qi < q.items.length; qi++) if (q.items[qi].id === variantId) dup = true;
    if (!dup) q.items.push({ id: variantId, label: addLabel });
    busyOffers[parentKey + '|' + variantId] = true;
    q.surface = addBtn.closest('cart-drawer') ? 'drawer' : 'page';
    if (q.timer) clearTimeout(q.timer);
    if (!pending) q.timer = setTimeout(function () { drainUpsellQueue(); }, 350);
    return;
  });

  // One queued flight per parent; parents drain sequentially after each landing.
  var upsellQueue = {};
  // Queued/in-flight offers keep their spinner across section re-renders (owner: a tap
  // that lands mid-flight must NOT show a clean ADD while it waits for the next bundle —
  // that reads as "the tap was lost"). Keyed parentKey|variantId; cleared on landing.
  var busyOffers = {};
  function markQueuedBusy() {
    for (var bk in busyOffers) {
      if (!busyOffers[bk]) continue;
      var sep = bk.lastIndexOf('|');
      var pKey = bk.slice(0, sep);
      var btns = document.querySelectorAll(
        '[data-sc-cart-upsell-add][data-sc-upsell-variant="' + bk.slice(sep + 1) + '"]'
      );
      for (var bi = 0; bi < btns.length; bi++) {
        if (btns[bi].getAttribute('data-sc-upsell-parent') !== pKey) continue;
        btns[bi].setAttribute('aria-busy', 'true');
        var br = btns[bi].closest('.sc-cart-upsell__row');
        if (br) br.classList.add('updating');
      }
    }
  }
  function unmarkOfferBusy(parentKey, list) {
    for (var ui = 0; ui < list.length; ui++) {
      delete busyOffers[parentKey + '|' + list[ui].id];
      var btns = document.querySelectorAll(
        '[data-sc-cart-upsell-add][data-sc-upsell-variant="' + list[ui].id + '"]'
      );
      for (var bj = 0; bj < btns.length; bj++) {
        if (btns[bj].getAttribute('data-sc-upsell-parent') !== parentKey) continue;
        btns[bj].removeAttribute('aria-busy');
        var br2 = btns[bj].closest('.sc-cart-upsell__row');
        if (br2) br2.classList.remove('updating');
      }
    }
  }
  function drainUpsellQueue() {
    if (pending) return;
    var parentKey = null;
    for (var k in upsellQueue) {
      if (upsellQueue[k] && upsellQueue[k].items.length) { parentKey = k; break; }
    }
    if (parentKey === null) return;
    var q = upsellQueue[parentKey];
    delete upsellQueue[parentKey];
    runUpsellFlight(parentKey, q.items, q.surface || 'drawer');
  }

  function runUpsellFlight(parentKey, companions, addSurface) {
    try { window.__scUpsellFlights = (window.__scUpsellFlights || 0) + 1; } catch (eDbg) { /* QA only */ }
    var addLabel = companions.map(function (c) { return c.label; }).join(' + ');
    pending = true;
    setTogglesDisabled(true);
    var row = document.querySelector('.sc-cart-upsell__row.updating');

    // Checkout draws the child under its parent ONLY for native parent_relationship lines,
    // and Shopify creates those ONLY when parent and children arrive in the SAME add call
    // (nesting onto an existing line 422s — proved on DRAFT). So: capture the parent line,
    // remove it, then re-add parent + every companion bundled. Same variant+properties →
    // same line key, so the carried preview state still matches. On any failure the
    // captured parent is restored before surfacing the error.
    var capturedParent = null;

    whenCartIdle(3000)
      .then(function () {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var line = 0;
        var parent = null;
        var keyCount = {};
        for (var i = 0; i < cart.items.length; i++) {
          keyCount[cart.items[i].key] = (keyCount[cart.items[i].key] || 0) + 1;
          if (!parent && cart.items[i].key === parentKey && !cart.items[i].parent_relationship) {
            parent = cart.items[i];
            line = i + 1;
          }
        }
        if (!parent) throw new Error('parent line not found');
        var stringProps = function (source) {
          var out = {};
          Object.keys(source || {}).forEach(function (k) {
            var v = source[k];
            if (v === null || typeof v === 'undefined') return;
            out[k] = String(v);
          });
          return out;
        };
        // Removing the parent CASCADES to its native children — an earlier companion (e.g. the
        // Shipping Protection just added) would silently vanish from the order. Capture every
        // existing child and re-add the whole family bundled with the new companion.
        var children = [];
        for (var ci = 0; ci < cart.items.length; ci++) {
          var it = cart.items[ci];
          // AJAX cart.js flattens the relationship to { parent_key } (the Liquid drop's
          // .parent.key shape does NOT exist here — proved on DRAFT: children were never
          // captured and the first companion vanished on the second ADD).
          var rel = it.parent_relationship;
          var relKey = rel && (rel.parent_key || (rel.parent && rel.parent.key));
          if (relKey === parentKey) {
            // Companions are qty-1 by contract — carrying a merged double forward would
            // preserve it forever, so the bundle also HEALS any accidental qty>1.
            children.push({ id: it.variant_id, quantity: 1, properties: stringProps(it.properties) });
          }
        }
        // ONCE-ONLY (owner rule "chỉ 1"): a queued companion the parent already carries is
        // dropped here against the FRESH cart read — a tap that raced a landing flight (the
        // stale row was still visible) would otherwise add a second copy that Shopify merges
        // into qty 2. If nothing is left to add, the whole mutation is skipped.
        companions = companions.filter(function (c) {
          for (var di = 0; di < children.length; di++) {
            if (String(children[di].id) === String(c.id)) {
              unmarkOfferBusy(parentKey, [c]);
              return false;
            }
          }
          return true;
        });
        if (!companions.length) {
          var skipErr = new Error('all queued companions already present');
          skipErr.__scSkipBenign = true;
          throw skipErr;
        }
        capturedParent = {
          id: parent.variant_id,
          quantity: parent.quantity,
          properties: stringProps(parent.properties),
          children: children,
          sellingPlan: parent.selling_plan_allocation && parent.selling_plan_allocation.selling_plan
            ? parent.selling_plan_allocation.selling_plan.id
            : null
        };
        // Unique key → key-based removal, immune to index shifts (an index landing on
        // another line would delete it with no restore path).
        var removeBody = keyCount[parentKey] === 1
          ? { id: parentKey, quantity: 0 }
          : { line: line, quantity: 0 };
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(removeBody)
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('parent remove failed ' + res.status);
        return res.json();
      })
      .then(function (json) {
        var still = (json.items || []).some(function (it) {
          return it.key === parentKey && !it.parent_relationship;
        });
        if (still) throw new Error('parent line index shifted');
        var body = new URLSearchParams();
        body.set('items[0][id]', String(capturedParent.id));
        body.set('items[0][quantity]', String(capturedParent.quantity));
        if (capturedParent.sellingPlan) {
          body.set('items[0][selling_plan]', String(capturedParent.sellingPlan));
        }
        Object.keys(capturedParent.properties).forEach(function (k) {
          body.set('items[0][properties][' + k + ']', capturedParent.properties[k]);
        });
        // 61325+ = this store's proven bracket shape for companion indexes (nested on draft).
        for (var ni = 0; ni < companions.length; ni++) {
          var nslot = 'items[' + (61325 + ni) + ']';
          body.set(nslot + '[id]', String(companions[ni].id));
          body.set(nslot + '[quantity]', '1');
          body.set(nslot + '[parent_id]', String(capturedParent.id));
        }
        // The parent's EXISTING children ride along in the same call, or they are lost.
        for (var xi = 0; xi < capturedParent.children.length; xi++) {
          var child = capturedParent.children[xi];
          var slot = 'items[' + (61325 + companions.length + xi) + ']';
          body.set(slot + '[id]', String(child.id));
          body.set(slot + '[quantity]', String(child.quantity));
          body.set(slot + '[parent_id]', String(capturedParent.id));
          Object.keys(child.properties).forEach(function (k) {
            body.set(slot + '[properties][' + k + ']', child.properties[k]);
          });
        }
        body.set('sections', sectionsToRender());
        body.set('sections_url', window.location.pathname);
        return postFormXhr('/cart/add.js', body);
      })
      .then(function (res) {
        if (!res.ok) throw new Error('nested add failed ' + res.status);
        return res.json();
      })
      .then(function (json) {
        unmarkOfferBusy(parentKey, companions);
        swapSections(json.sections);
        setTogglesDisabled(false);
        showToast(addLabel + ' added', addSurface);
        focusParentToggle(parentKey, addSurface);
      })
      .catch(function (flightError) {
        if (flightError && flightError.__scSkipBenign) {
          // Nothing was mutated (the skip fires before the remove) — just refresh the
          // sections so the stale offer row folds away.
          return resyncCartSections().then(function () {
            setTogglesDisabled(false);
          }, function () { setTogglesDisabled(false); });
        }
        // The parent may already be gone (removed but bundled re-add failed) — a
        // personalized line must never be lost to an upsell ADD. Verify from a fresh
        // read and put it back before reporting the error.
        var recovery = !capturedParent
          ? Promise.resolve(true)
          : fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (res) { return res.json(); })
              .then(function (cart) {
                var back = (cart.items || []).some(function (it) { return it.key === parentKey; });
                if (back) return true;
                // Same form-encoded family bundle as the forward path: parent + every
                // captured child, so a failed ADD never costs an existing companion.
                var rb = new URLSearchParams();
                rb.set('items[0][id]', String(capturedParent.id));
                rb.set('items[0][quantity]', String(capturedParent.quantity));
                if (capturedParent.sellingPlan) rb.set('items[0][selling_plan]', String(capturedParent.sellingPlan));
                Object.keys(capturedParent.properties).forEach(function (k) {
                  rb.set('items[0][properties][' + k + ']', capturedParent.properties[k]);
                });
                for (var ri = 0; ri < capturedParent.children.length; ri++) {
                  var rc = capturedParent.children[ri];
                  var rslot = 'items[' + (61325 + ri) + ']';
                  rb.set(rslot + '[id]', String(rc.id));
                  rb.set(rslot + '[quantity]', String(rc.quantity));
                  rb.set(rslot + '[parent_id]', String(capturedParent.id));
                  Object.keys(rc.properties).forEach(function (k) {
                    rb.set(rslot + '[properties][' + k + ']', rc.properties[k]);
                  });
                }
                rb.set('sections', sectionsToRender());
                rb.set('sections_url', window.location.pathname);
                return postFormXhr('/cart/add.js', rb)
                  .then(function (res) { return res.ok ? res.json() : null; })
                  .then(function (json2) {
                    if (!json2) return false;
                    swapSections(json2.sections);
                    return true;
                  });
              })
              .catch(function () { return false; });
        return recovery.then(function (restored) {
          var finish = function () {
            var freshRow = row && row.isConnected ? row : document.querySelector('.sc-cart-upsell__row');
            if (freshRow) freshRow.classList.remove('updating');
            unmarkOfferBusy(parentKey, companions);
            setTogglesDisabled(false);
            showError(freshRow, restored
              ? 'Could not add ' + addLabel + '. Please try again.'
              : 'Your item could not be updated. Please review your cart.');
          };
          if (restored) { finish(); return; }
          return resyncCartSections().then(finish, finish);
        });
      })
      .then(function () {
        pending = false;
        drainUpsellQueue(); // taps that landed mid-flight ship in the next bundle
      });
  }

  // ---- Cart upsell block: MagSafe row reveal (sc-cart-upsell-block.liquid) --------------
  // Liquid emits every top-level candidate line {key, model, hasMagsafe, bundled} plus the
  // shop's capable-model list (sc_upsell.magsafe_models, synced from the release condition).
  // The first candidate whose model is capable and does not already carry/bundle MagSafe
  // claims the hidden row. Runs on every DOM change that (re)inserts an unprocessed block —
  // section re-renders replace the markup wholesale, so a MutationObserver is the one hook
  // that catches every render path (vendor swaps, PZ renderSections, our swapSections).
  // Squash-key: the regular and tough releases label the SAME model differently
  // ("iPhone 17 Pro Max" vs "iPhone 17 ProMax", "A13 4G" vs "A13 (4G)") — mapped 2026-08-19,
  // 14 models were invisible to the gates on formatting alone. Comparing on lowercase
  // alphanumerics only makes both sides of the lookup immune to spacing/punctuation drift.
  var normModel = function (v) {
    return String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  };
  // (Owner rev 3: the earlier per-line dismissal memory is GONE — a removed companion's
  // offer returns to the box and waits in case the shopper changes their mind.)
  var COLLAPSE_KEY = 'sc_upsell_collapsed';
  function isCollapsed() {
    try { return sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch (e) { return false; }
  }
  function setCollapsed(v) {
    try { sessionStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch (e) { /* no-op */ }
  }
  function applyCollapse(box, collapsed) {
    box.classList.toggle('sc-cart-upsell--collapsed', collapsed);
    var head = box.querySelector('[data-sc-upsell-toggle]');
    if (head) head.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }
  document.addEventListener('click', function (e) {
    var head = e.target && e.target.closest && e.target.closest('[data-sc-upsell-toggle]');
    if (!head) return;
    var box = head.closest('[data-sc-cart-upsell]');
    if (!box) return;
    var collapsed = !box.classList.contains('sc-cart-upsell--collapsed');
    setCollapsed(collapsed);
    // Every box on the page follows (drawer + cart page render in parallel).
    document.querySelectorAll('[data-sc-cart-upsell]').forEach(function (b) { applyCollapse(b, collapsed); });
  });
  // Dashed separator between VISIBLE product cards only. CSS li+li mis-fires when a
  // hidden or vendor li precedes the first card, so JS decides per render pass.
  function markCardSeparators() {
    // VISUAL order, not DOM order: scCartRowOrder pins the newest row to the top with flex
    // `order`, so the DOM-second card can render FIRST — a DOM-walk put the dash above the
    // top card (owner: "dash sai vị trí"). Sort by on-screen position; every card below the
    // visually-first one carries the separator.
    var mark = function (nodes) {
      var visible = [];
      nodes.forEach(function (el) {
        var ok = !el.hidden && el.getClientRects().length > 0
          && !el.classList.contains('sc-cart-updates-placeholder');
        if (ok) visible.push(el);
        else el.classList.remove('sc-cart-sep');
      });
      visible.sort(function (a, b) { return a.getBoundingClientRect().top - b.getBoundingClientRect().top; });
      visible.forEach(function (el, i) { el.classList.toggle('sc-cart-sep', i > 0); });
    };
    document.querySelectorAll('cart-drawer .cart-items').forEach(function (list) {
      mark(list.querySelectorAll(':scope > li.cart-drawer-item'));
    });
    // The cart PAGE marks its separators SERVER-SIDE in Liquid (no row pinning there, so
    // the render order IS the visual order): zero-delay, product rows only — the earlier
    // JS pass painted after first load and could catch helper rows (owner).
  }

  function revealMagsafeOffers(root) {
    markCardSeparators();
    var blocks = (root || document).querySelectorAll('[data-sc-cart-upsell]:not([data-sc-upsell-ready])');
    for (var i = 0; i < blocks.length; i++) {
      var box = blocks[i];
      box.setAttribute('data-sc-upsell-ready', 'true');
      var dataTag = box.querySelector('[data-sc-upsell-magsafe-data]');
      if (!dataTag) continue;
      var parsed = null;
      try { parsed = JSON.parse(dataTag.textContent); } catch (err) { parsed = null; }
      if (!parsed || !Array.isArray(parsed.candidates) || !Array.isArray(parsed.capableModels)) continue;
      var capable = {};
      for (var c = 0; c < parsed.capableModels.length; c++) capable[normModel(parsed.capableModels[c])] = true;
      var toughable = {};
      var toughList = Array.isArray(parsed.toughModels) ? parsed.toughModels : [];
      for (var tc = 0; tc < toughList.length; tc++) toughable[normModel(toughList[tc])] = true;
      applyCollapse(box, isCollapsed());
      var unveil = function (rowName, label, fits) {
        var row = box.querySelector('[data-sc-upsell-row="' + rowName + '"]');
        if (!row) return;
        var winner = null;
        for (var k = 0; k < parsed.candidates.length; k++) {
          var cand = parsed.candidates[k];
          if (!cand) continue;
          if (!fits(cand)) continue;
          winner = cand;
          break;
        }
        if (!winner) return; // row stays hidden
        var btn = row.querySelector('[data-sc-cart-upsell-add]');
        if (!btn) return;
        btn.setAttribute('data-sc-upsell-parent', winner.key);
        row.hidden = false;
        row.removeAttribute('hidden');
      };
      unveil('protection', 'Shipping Protection', function () { return true; });
      // Tough upgrade: REGULAR case lines only (a native Tough product has nothing to upgrade),
      // gated on the model existing in the Tough catalog (sc_upsell.tough_models).
      unveil('tough', 'Tough', function (cand) {
        return cand.tough !== true && !cand.hasTough && !cand.bundled
          && cand.phoneCase === true
          && cand.model && toughable[normModel(cand.model)] === true;
      });
      // MagSafe snaps onto the TOUGH shell only — a regular (CustomAll) case line must never
      // see this offer even when its chosen model is MagSafe-capable (owner, anh ndlj).
      // A regular case UPGRADED with the make-it-tough companion counts as tough too
      // (owner: adding the Tough upsell unlocks the MagSafe offer for capable models).
      unveil('magsafe', 'MagSafe', function (cand) {
        return (cand.tough === true || cand.hasTough === true) && !cand.hasMagsafe && !cand.bundled
          && cand.model && capable[normModel(cand.model)] === true;
      });
      // Nothing to offer (all present or dismissed) -> no orphan heading floating in the cart.
      var visible = box.querySelectorAll('.sc-cart-upsell__row:not([hidden])');
      box.style.display = visible.length ? '' : 'none';
    }
    // Fresh markup renders clean ADD buttons — re-apply the spinner on offers that are
    // still queued or airborne so a waiting tap never looks dropped.
    markQueuedBusy();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { revealMagsafeOffers(document); });
  } else {
    revealMagsafeOffers(document);
  }
  try {
    new MutationObserver(function () { revealMagsafeOffers(document); })
      .observe(document.body || document.documentElement, { childList: true, subtree: true });
  } catch (err) { /* reveal still runs on initial load */ }
})();
